import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createLoanCrudHooks } from './lib/create-crud-hooks';
import { invalidateLoanActivity } from './lib/invalidate-loan-activity';
import { api } from '@/lib/api';

export interface SettlementStatement {
  id: string;
  loanId: string;
  statementNumber: string;
  statementDate: string;
  status: 'DRAFT' | 'ISSUED' | 'RECONCILED' | 'DISPUTED';
  loanAdvance: string;
  establishmentFee?: string;
  brokerageFee?: string;
  legalCosts?: string;
  titleInsurance?: string;
  valuationFee?: string;
  stampDuty?: string;
  otherDeductions?: string;
  otherDeductionsDesc?: string;
  netAdvance: string;
  totalDeductions: string;
  reconciledAt?: string;
  reconciledById?: string;
  reconciliationNotes?: string;
  variance?: string;
  documentUrl?: string;
  notes?: string;
  createdAt: string;
  loan?: any;
  reconciledBy?: { id: string; firstName: string; lastName: string };
}

// ── Factory CRUD hooks ───────────────────────────────────

const { useList, useDelete: _useDelete } = createLoanCrudHooks<SettlementStatement, any, any>(
  'settlements',
  'settlement',
);

export function useSettlements(loanId: string) {
  return useList(loanId);
}

export function useCreateSettlement(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: any) => {
      const { data } = await api.post(`/loans/${loanId}/settlement`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settlements', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// ── Update settlement (manual - uses sid param not id) ───

export function useUpdateSettlement(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sid, ...input }: any) => {
      const { data } = await api.patch(`/loans/${loanId}/settlement/${sid}`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settlements', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// ── Reconcile settlement (manual - custom action) ───────

export function useReconcileSettlement(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sid, notes }: { sid: string; notes?: string }) => {
      const { data } = await api.post(`/loans/${loanId}/settlement/${sid}/reconcile`, { notes });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settlements', loanId] });
      qc.invalidateQueries({ queryKey: ['loans', loanId] });
      invalidateLoanActivity(qc, loanId);
    },
  });
}
